import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CLASS_STYLES } from "@/lib/match";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { TrendingUp, Repeat2, GitCompareArrows, Users2 } from "lucide-react";

const TEAL = "hsl(175 77% 26%)";
const GOLD = "hsl(46 65% 52%)";

export default function Analytics() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/analytics/overrides").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="p-8">Loading…</div>;

  const empty = data.total_overrides === 0;
  const KPIS = [
    { label: "AI matches generated", value: data.total_matches, icon: GitCompareArrows, gold: false },
    { label: "Recruiter overrides", value: data.total_overrides, icon: Repeat2, gold: false },
    { label: "Override rate", value: `${data.override_rate}%`, icon: TrendingUp, gold: true },
    { label: "Recruiters correcting", value: data.by_recruiter.length, icon: Users2, gold: false },
  ];

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8 fade-up">
        <div className="text-xs uppercase tracking-widest text-[hsl(var(--teal))] font-semibold">Quality Improvement</div>
        <h1 className="font-head text-3xl font-semibold tracking-tight mt-1">Match Quality Analytics</h1>
        <p className="text-muted-foreground mt-2">Learned from every recruiter override — showing where the AI most often needs adjustment.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {KPIS.map(({ label, value, icon: Icon, gold }, i) => (
          <div key={label} data-testid={`analytics-kpi-${i}`} className="card-hover bg-white border border-border rounded-xl p-6 fade-up" style={{ animationDelay: `${i * 40}ms` }}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{label}</span>
              <Icon size={18} className={gold ? "text-[hsl(var(--gold))]" : "text-[hsl(var(--teal))]"} />
            </div>
            <div className={`font-head text-3xl font-semibold mt-3 tracking-tight ${gold ? "text-[hsl(var(--gold))]" : ""}`}>{value}</div>
          </div>
        ))}
      </div>

      {empty ? (
        <div className="text-center py-20 border border-dashed border-border rounded-xl">
          <p className="text-muted-foreground">No overrides recorded yet. As recruiters correct AI classifications on candidate pages, this report will reveal the AI's blind spots.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-border rounded-xl p-6">
            <h3 className="font-head font-bold text-lg mb-1">Most-corrected AI classifications</h3>
            <p className="text-sm text-muted-foreground mb-4">Where the AI's original call gets overturned most often.</p>
            <ResponsiveContainer width="100%" height={Math.max(140, data.most_corrected_from.length * 46)}>
              <BarChart data={data.most_corrected_from} layout="vertical" margin={{ left: 10, right: 24 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="classification" width={150} tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "hsl(var(--muted))" }} formatter={(v, n, p) => [`${v} overrides (${p.payload.percent}%)`, "Count"]} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {data.most_corrected_from.map((e, i) => <Cell key={i} fill={i === 0 ? GOLD : TEAL} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-border rounded-xl p-6">
            <h3 className="font-head font-bold text-lg mb-4">Correction patterns</h3>
            <div className="space-y-2.5">
              {data.transitions.map((t, i) => {
                const [from, to] = t.transition.split(" -> ");
                return (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Badge variant="outline" className={`text-xs border ${CLASS_STYLES[from] || ""}`}>{from}</Badge>
                    <span className="text-muted-foreground">→</span>
                    <Badge variant="outline" className={`text-xs border ${CLASS_STYLES[to] || ""}`}>{to}</Badge>
                    <span className="ml-auto font-semibold">{t.count}×</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2 bg-white border border-border rounded-xl p-6">
            <h3 className="font-head font-bold text-lg mb-4">Recent overrides</h3>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>{["Candidate", "AI said", "Recruiter said", "Recruiter", "Date", "Note"].map((h) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.recent.map((r, i) => (
                    <tr key={i} data-testid={`override-row-${i}`} onClick={() => navigate(`/candidates/${r.candidate_id}`)} className="border-t border-border hover:bg-secondary/40 cursor-pointer transition-colors">
                      <td className="px-4 py-2.5 font-medium">{r.candidate_name || "—"}</td>
                      <td className="px-4 py-2.5"><Badge variant="outline" className={`text-xs border ${CLASS_STYLES[r.original] || ""}`}>{r.original}</Badge></td>
                      <td className="px-4 py-2.5"><Badge variant="outline" className={`text-xs border ${CLASS_STYLES[r.revised] || ""}`}>{r.revised}</Badge></td>
                      <td className="px-4 py-2.5 text-muted-foreground">{r.recruiter}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{r.date ? new Date(r.date).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-2.5 text-muted-foreground italic truncate max-w-[200px]">{r.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
